import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Modal, Platform, ScrollView } from 'react-native';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient, GradientOverlay } from './Gradients';
import GradientButton from './GradientButton';
import GradientText from './GradientText';

export default function SavedRequestsModal({ visible, onClose, onApplyRequest, userProfile, onSaveRequest, currentQuery }) {
    const [savedRequests, setSavedRequests] = useState([]);
    const [newShortcut, setNewShortcut] = useState('');
    const [newQuery, setNewQuery] = useState('');
    const [newBooks, setNewBooks] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    
    // Load saved requests from user profile
    useEffect(() => {
        if (userProfile?.preferences?.savedRequests) {
            setSavedRequests(userProfile.preferences.savedRequests);
        }
    }, [userProfile]);

    // Prepopulate form with current query when modal opens
    useEffect(() => {
        if (visible && currentQuery) {
            // Generate an inferred title from the query
            const inferredTitle = currentQuery
                .toLowerCase()
                .split(' ')
                .slice(0, 3) // Take first 3 words
                .join(' ');
            
            setNewShortcut(inferredTitle);
            setNewQuery(currentQuery);
            setShowAddForm(true);
        }
    }, [visible, currentQuery]);

    const handleSaveRequest = async () => {
        if (!newShortcut.trim() || !newQuery.trim()) return;

        const newRequest = {
            id: Date.now().toString(),
            shortcut: newShortcut.trim().toLowerCase(),
            query: newQuery.trim(),
            books: newBooks.trim() || null,
            createdAt: new Date().toISOString()
        };

        const updatedRequests = [...savedRequests, newRequest];
        setSavedRequests(updatedRequests);
        
        // Save to user profile
        if (onSaveRequest) {
            await onSaveRequest(updatedRequests);
        }

        // Reset form
        setNewShortcut('');
        setNewQuery('');
        setNewBooks('');
        setShowAddForm(false);
        
        // Close modal after saving
        onClose();
    };

    const handleDeleteRequest = async (id) => {
        const updatedRequests = savedRequests.filter(req => req.id !== id);
        setSavedRequests(updatedRequests);
        
        if (onSaveRequest) {
            await onSaveRequest(updatedRequests);
        }
    };

    const handleApplyRequest = (request) => {
        if (onApplyRequest) {
            onApplyRequest(request);
        }
        console.log('SavedRequestsModal: Apply request, closing modal');
        onClose();
    };
    
    const handleClose = () => {
        console.log('SavedRequestsModal: handleClose called');
        setShowAddForm(false);
        setNewShortcut('');
        setNewQuery('');
        setNewBooks('');
        onClose();
    };

    if (!visible) return null;

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={handleClose}
        >
            <TouchableOpacity 
                style={styles.modalOverlay}
                activeOpacity={1}
                onPress={handleClose}
            >
                <BlurView intensity={60} tint="dark" style={styles.modalBlur}>
                    <TouchableOpacity
                        activeOpacity={1}
                        onPress={(e) => e.stopPropagation()}
                        style={styles.modalContainer}
                    >
                        <GlassBackgroundGradient borderRadius={20} />
                        <GradientOverlay opacity={0.3} borderRadius={20} />
                        
                        <View style={styles.modalContent}>
                            <View style={styles.modalHeader}>
                                <GradientText style={styles.modalTitle}>
                                    Saved Requests
                                </GradientText>
                                <Text style={styles.modalDescription}>
                                    Quick shortcuts for your favorite betting queries
                                </Text>
                            </View>

                            <ScrollView style={styles.requestsList}>
                                {savedRequests.map((request) => (
                                    <View key={request.id} style={styles.requestItem}>
                                        <View style={styles.requestInfo}>
                                            <Text style={styles.requestShortcut}>{request.shortcut}</Text>
                                            <Text style={styles.requestQuery}>{request.query}</Text>
                                            {request.books && (
                                                <Text style={styles.requestBooks}>Books: {request.books}</Text>
                                            )}
                                        </View>
                                        <View style={styles.requestActions}>
                                            <TouchableOpacity
                                                onPress={() => handleApplyRequest(request)}
                                                style={styles.applyBtn}
                                            >
                                                <Text style={styles.applyBtnText}>Use</Text>
                                            </TouchableOpacity>
                                            <TouchableOpacity
                                                onPress={() => handleDeleteRequest(request.id)}
                                                style={styles.deleteBtn}
                                            >
                                                <Text style={styles.deleteBtnText}>×</Text>
                                            </TouchableOpacity>
                                        </View>
                                    </View>
                                ))}
                                
                                {savedRequests.length === 0 && (
                                    <View style={styles.emptyState}>
                                        <Text style={styles.emptyStateText}>No saved requests yet</Text>
                                        <Text style={styles.emptyStateSubtext}>Create shortcuts for your favorite queries</Text>
                                    </View>
                                )}
                            </ScrollView>

                            {!showAddForm ? (
                                <View style={styles.buttonContainer}>
                                    <GradientButton
                                        text="Add New Shortcut"
                                        onPress={() => setShowAddForm(true)}
                                        style={styles.addButton}
                                    />
                                    <TouchableOpacity
                                        onPress={(e) => {
                                            e.stopPropagation();
                                            handleClose();
                                        }}
                                        style={styles.closeButton}
                                    >
                                        <Text style={styles.closeButtonText}>Close</Text>
                                    </TouchableOpacity>
                                </View>
                            ) : (
                                <View style={styles.addForm}>
                                    <Text style={styles.formTitle}>Add New Shortcut</Text>
                                    
                                    <View style={styles.inputGroup}>
                                        <Text style={styles.inputLabel}>Shortcut (e.g., "fd nba")</Text>
                                        <TextInput
                                            style={styles.textInput}
                                            value={newShortcut}
                                            onChangeText={setNewShortcut}
                                            placeholder="fd nba"
                                            placeholderTextColor="rgba(255,255,255,0.5)"
                                        />
                                    </View>

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.inputLabel}>Full Query</Text>
                                        <TextInput
                                            style={styles.textInput}
                                            value={newQuery}
                                            onChangeText={setNewQuery}
                                            placeholder="NBA games tonight FanDuel"
                                            placeholderTextColor="rgba(255,255,255,0.5)"
                                            multiline
                                        />
                                    </View>

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.inputLabel}>Books (optional)</Text>
                                        <TextInput
                                            style={styles.textInput}
                                            value={newBooks}
                                            onChangeText={setNewBooks}
                                            placeholder="FanDuel, DraftKings, Caesars"
                                            placeholderTextColor="rgba(255,255,255,0.5)"
                                        />
                                    </View>

                                    <View style={styles.formActions}>
                                        <TouchableOpacity
                                            onPress={(e) => {
                                                e.stopPropagation();
                                                console.log('SavedRequestsModal: Cancel button pressed');
                                                setShowAddForm(false);
                                                setNewShortcut('');
                                                setNewQuery('');
                                                setNewBooks('');
                                            }}
                                            style={styles.cancelBtn}
                                        >
                                            <Text style={styles.cancelBtnText}>Cancel</Text>
                                        </TouchableOpacity>
                                        <GradientButton
                                            text="Save"
                                            onPress={(e) => {
                                                e?.stopPropagation?.();
                                                handleSaveRequest();
                                            }}
                                            style={styles.saveBtn}
                                        />
                                    </View>
                                </View>
                            )}

                            {/* Save Current Query Button (shown when no form is open) */}
                            {!showAddForm && currentQuery && (
                                <GradientButton
                                    text="Save Current Query"
                                    onPress={() => setShowAddForm(true)}
                                    style={styles.saveCurrentButton}
                                />
                            )}
                        </View>
                    </TouchableOpacity>
                </BlurView>
            </TouchableOpacity>
        </Modal>
    );
}

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalBlur: {
        width: '100%',
        maxWidth: 500,
        maxHeight: '80%',
        borderRadius: 20,
        overflow: 'hidden',
    },
    modalContainer: {
        position: 'relative',
        padding: 24,
        minHeight: 400,
    },
    modalContent: {
        position: 'relative',
        zIndex: 2,
    },
    modalHeader: {
        marginBottom: 20,
        alignItems: 'center',
    },
    modalTitle: {
        fontSize: 24,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 8,
    },
    modalDescription: {
        fontSize: 16,
        color: 'rgba(255, 255, 255, 0.8)',
        textAlign: 'center',
        lineHeight: 22,
    },
    requestsList: {
        maxHeight: 300,
        marginBottom: 20,
    },
    requestItem: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 16,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 8,
        marginBottom: 8,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    requestInfo: {
        flex: 1,
    },
    requestShortcut: {
        color: '#4ade80',
        fontSize: 16,
        fontWeight: '700',
        marginBottom: 4,
    },
    requestQuery: {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: 14,
        marginBottom: 2,
    },
    requestBooks: {
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: 12,
    },
    requestActions: {
        flexDirection: 'row',
        gap: 8,
    },
    applyBtn: {
        paddingVertical: 6,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(74, 222, 128, 0.15)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#4ade80',
    },
    applyBtnText: {
        color: '#4ade80',
        fontSize: 12,
        fontWeight: '600',
    },
    deleteBtn: {
        paddingVertical: 6,
        paddingHorizontal: 10,
        backgroundColor: 'rgba(248, 113, 113, 0.15)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#f87171',
    },
    deleteBtnText: {
        color: '#f87171',
        fontSize: 16,
        fontWeight: '700',
    },
    emptyState: {
        alignItems: 'center',
        paddingVertical: 40,
    },
    emptyStateText: {
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: 16,
        marginBottom: 8,
    },
    emptyStateSubtext: {
        color: 'rgba(255, 255, 255, 0.4)',
        fontSize: 14,
    },
    buttonContainer: {
        gap: 12,
        marginBottom: 16,
    },
    addButton: {
        height: 48,
    },
    closeButton: {
        height: 48,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    closeButtonText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 16,
        fontWeight: '500',
    },
    addForm: {
        marginBottom: 16,
    },
    formTitle: {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: 18,
        fontWeight: '600',
        marginBottom: 16,
    },
    inputGroup: {
        marginBottom: 16,
    },
    inputLabel: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 14,
        marginBottom: 8,
    },
    textInput: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        fontSize: 14,
        color: 'white',
    },
    formActions: {
        flexDirection: 'row',
        gap: 12,
    },
    cancelBtn: {
        flex: 1,
        height: 48,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    cancelBtnText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 16,
        fontWeight: '500',
    },
    saveBtn: {
        flex: 1,
        height: 48,
    },
    saveCurrentButton: {
        height: 48,
        marginTop: 16,
    },
});
